import { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

export type ProductTourStep = {
  target: string;
  title?: string;
  description?: string;
};

type ProductTourProps = {
  steps: ProductTourStep[];
  onClose: () => void;
  completionLabel?: string;
  onComplete?: () => void;
};

type TargetRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

const getTargetRect = (selector: string): TargetRect | null => {
  const target = document.querySelector<HTMLElement>(selector);
  if (!target) return null;
  const rect = target.getBoundingClientRect();
  const top = Math.max(8, rect.top - 6);
  const left = Math.max(8, rect.left - 6);
  const right = Math.min(window.innerWidth - 8, rect.right + 6);
  const bottom = Math.min(window.innerHeight - 8, rect.bottom + 6);
  return {
    top,
    left,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  };
};

const sameRect = (left: TargetRect | null, right: TargetRect | null): boolean => (
  left === right
  || left !== null
    && right !== null
    && left.top === right.top
    && left.left === right.left
    && left.width === right.width
    && left.height === right.height
);

export function ProductTour({ steps, onClose, completionLabel = '完了', onComplete = onClose }: ProductTourProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const step = steps[stepIndex];

  useLayoutEffect(() => {
    if (!step) return;
    const target = document.querySelector<HTMLElement>(step.target);
    target?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    const update = () => {
      const nextRect = getTargetRect(step.target);
      setTargetRect((currentRect) => sameRect(currentRect, nextRect) ? currentRect : nextRect);
    };
    let animationFrameId = 0;
    const trackTarget = () => {
      update();
      animationFrameId = window.requestAnimationFrame(trackTarget);
    };
    update();
    animationFrameId = window.requestAnimationFrame(trackTarget);
    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [step]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight' && stepIndex < steps.length - 1) setStepIndex((current) => current + 1);
      if (event.key === 'ArrowLeft' && stepIndex > 0) setStepIndex((current) => current - 1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, stepIndex, steps.length]);

  if (!step) return null;
  const tooltipStyle = targetRect
    ? {
        left: Math.min(Math.max(12, targetRect.left), Math.max(12, window.innerWidth - 332)),
        top: targetRect.top + targetRect.height + 12,
      }
    : undefined;
  const placeAbove = targetRect && tooltipStyle && tooltipStyle.top + 210 > window.innerHeight;
  if (placeAbove) tooltipStyle.top = Math.max(12, targetRect.top - 198);

  return createPortal(
    <div
      className="product-tour"
      role="dialog"
      aria-modal="true"
      aria-label="使い方ガイド"
      onClick={(event) => event.stopPropagation()}
    >
      <div className="product-tour__shade" />
      {targetRect && <div className="product-tour__highlight" style={targetRect} />}
      <section className={`product-tour__tooltip${targetRect ? '' : ' is-centered'}`} style={tooltipStyle}>
        <header className="product-tour__header">
          <span> {steps.length > 1 && `${stepIndex + 1} / ${steps.length}`}</span>
          <button type="button" onClick={onClose} aria-label="ガイドを閉じる" title="ガイドを閉じる">
            <X aria-hidden="true" size={18} />
          </button>
        </header>
        {step.title && <h2>{step.title}</h2>}
        {step.description && <p>{step.description}</p>}
        <footer className="product-tour__actions">
          <button
            type="button"
            className="product-tour__button is-secondary"
            onClick={() => setStepIndex((current) => current - 1)}
            disabled={stepIndex === 0}
          >
            戻る
          </button>
          <button
            type="button"
            className="product-tour__button"
            onClick={() => {
              if (stepIndex === steps.length - 1) onComplete();
              else setStepIndex((current) => current + 1);
            }}
          >
            {stepIndex === steps.length - 1 ? completionLabel : '次へ'}
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  );
}
